import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
 

const services = [
  {
    icon: 'fa-solid fa-magnifying-glass-chart',
    title: '1. Search Engine Optimization (SEO)',
    desc: 'Boost your website ranking on search engines with technical SEO, keyword optimization, quality link building, and high-converting content strategies.',
  },
  {
    icon: 'fa-solid fa-rectangle-ad',
    title: '2. Pay-Per-Click (PPC) Advertising',
    desc: 'Drive instant, targeted traffic and maximize ROI with precision Google Ads, Bing Ads, and display advertising campaigns.',
  },
  {
    icon: 'fa-solid fa-share-nodes',
    title: '3. Social Media Marketing (SMM)',
    desc: 'Build a strong brand presence and engage your target audience across LinkedIn, Meta, Instagram, Twitter/X, and YouTube.',
  },
  {
    icon: 'fa-solid fa-pen-nib',
    title: '4. Content Marketing & Strategy',
    desc: 'Attract and retain customers through compelling blogs, whitepapers, case studies, videos, and infographics tailored to your industry.',
  },
  {
    icon: 'fa-solid fa-envelope-open-text',
    title: '5. Email Marketing & Automation',
    desc: 'Convert leads into loyal customers with automated email drip campaigns, personalized newsletters, and targeted lifecycle workflows.',
  },
  {
    icon: 'fa-solid fa-bullseye',
    title: '6. Conversion Rate Optimization (CRO)',
    desc: 'Turn existing traffic into paying clients by testing, analyzing, and optimizing your landing pages, CTAs, and user conversion funnels.',
  },
  {
    icon: 'fa-solid fa-chart-line-up-down',
    title: '7. Online Reputation Management (ORM)',
    desc: 'Protect and elevate your brand image online by managing reviews, monitoring customer feedback, and building digital trust.',
  },
  {
    icon: 'fa-solid fa-chart-pie',
    title: '8. Performance Analytics & Reporting',
    desc: 'Gain actionable business insights with real-time tracking, Google Analytics 4 integration, and transparent ROI reporting dashboards.',
  },
  {
    icon: 'fa-solid fa-users-viewfinder',
    title: '9. Account-Based Marketing (ABM)',
    desc: 'Target high-value B2B accounts with highly personalized digital experiences, precise ad targeting, and aligned sales campaigns.',
  },
  {
    icon: 'fa-solid fa-store',
    title: '10. Local SEO & Maps Optimization',
    desc: 'Dominate local search results, optimize your Google Business Profile, and attract nearby customers ready to buy.',
  },
];

const whyChooseUs = [
  'Data-driven marketing strategies',
  'Targeted ROI and lead generation',
  'Transparent, real-time analytics',
  'Multi-channel campaign execution',
  'Dedicated digital marketing team',
  'Continuous A/B conversion testing',
  'B2B and B2C specialized growth plans',
  'End-to-end sales funnel alignment',
  'Omnichannel brand experience',
  'Scalable traffic and revenue growth',
];

const processSteps = [
  { step: '01', title: 'Audit & Research', desc: 'Analyze your current digital presence, competitors, target audience, and keyword landscape.' },
  { step: '02', title: 'Strategy & Planning', desc: 'Craft a custom multi-channel growth blueprint aligned with your business goals.' },
  { step: '03', title: 'Campaign Setup', desc: 'Build landing pages, design creative assets, set up tracking, and configure ad platforms.' },
  { step: '04', title: 'Execution', desc: 'Launch targeted SEO, PPC, social media, and content marketing campaigns.' },
  { step: '05', title: 'Optimize & Test', desc: 'Monitor live data, run A/B tests, adjust bidding strategies, and optimize conversion rates.' },
  { step: '06', title: 'Scale & Report', desc: 'Scale winning channels, reallocate budget for maximum ROI, and deliver clear monthly reports.' },
];

const techStack = [
  { category: 'SEO & Content Tools', items: ['SEMrush', 'Ahrefs', 'Google Search Console', 'Yoast SEO', 'Surfer SEO'] },
  { category: 'Ads & Social Platforms', items: ['Google Ads', 'Meta Ads Manager', 'LinkedIn Campaign Manager', 'TikTok Ads'] },
  { category: 'Analytics & CRO', items: ['Google Analytics 4', 'Hotjar', 'Google Tag Manager', 'Looker Studio'] },
  { category: 'Automation & Email', items: ['HubSpot', 'Mailchimp', 'Klaviyo', 'ActiveCampaign'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const DigitalMarketing = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Reach. Engage. Convert.
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Digital <span className="gradient-text-cyan">Marketing Solutions</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              Accelerate your brand growth, acquire high-value leads, and maximize marketing ROI through data-driven campaigns, target-focused SEO, PPC, and social media strategies.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Grow Your Brand Today &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Our Digital <span className="gradient-text-orange">Marketing Services</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Us */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Choose Our <span className="gradient-text-cyan">Marketing Services?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Marketing Tools & Platforms</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Our Campaign <span className="gradient-text-orange">Process</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Ready to Scale Your Digital Growth?</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Whether you need to boost search rankings, acquire high-intent leads, or build an active social community, our marketing experts are here to drive quantifiable results.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Get a Free Growth Audit &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DigitalMarketing;